import React from 'react';

import HeartLoader from '@app/components/UI/HeartLoader/HeartLoader';
import { PhoneProviderContext } from '@app/providers/PhoneProvider/PhoneProvider';

interface BookingLoaderProps {
	loading: boolean;
	errorMessage: string;
	typeButton?: 'button' | 'submit';
	children?: React.ReactNode;
	forceDeleteTimer?: boolean;
	removeIfError?: boolean;
	onClick?: () => void;
}

const BookingLoader: React.FC<BookingLoaderProps> = ({
	loading,
	errorMessage,
	typeButton = 'button',
	children = null,
	forceDeleteTimer = false,
	removeIfError = true,
	onClick,
}) => {
	const contextPhone = React.useContext(PhoneProviderContext);

	if (!contextPhone?.state) throw new Error('Error PhoneProvider context');

	const timerRef = React.useRef<NodeJS.Timeout | null>(null);
	const [seconds, setSeconds] = React.useState<number>(0);

	const startTimer = React.useCallback(() => {
		if (timerRef.current) return;

		let count = 0;
		setSeconds(count);

		timerRef.current = setInterval(() => {
			count += 1;
			setSeconds(count);
		}, 1000);
	}, []);

	const clearTimer = React.useCallback((resetSeconds = false) => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
		if (resetSeconds) {
			setSeconds(0);
		}
	}, []);

	React.useEffect(() => {
		if (loading) {
			startTimer();
		}
		if (!loading && !errorMessage.length) {
			clearTimer(true);
		}
	}, [loading, startTimer, clearTimer]);

	React.useEffect(() => {
		if (errorMessage.length) {
			clearTimer(removeIfError);
		}
	}, [errorMessage, clearTimer]);

	React.useEffect(() => {
		if (seconds >= 20) {
			clearTimer();
		}
	}, [seconds, clearTimer]);

	React.useEffect(() => {
		if (forceDeleteTimer) {
			clearTimer(true);
		}
	}, [forceDeleteTimer, clearTimer]);

	return seconds > 0 || loading ? (
		errorMessage.length || seconds >= 20 ? (
			<div className="online-booking__error-code-component">
				<div className="text">
					<p>
						We’re sorry, but we’re having some technical difficulties. If you need immediate assistance,
						please call us at
						{' '}
						<a href={contextPhone.state.phoneNumberHref} className="inline-block inline-link">
							{`${contextPhone.state.phoneNumberDisplay}.`}
						</a>
					</p>
				</div>
				<button
					type={typeButton}
					className="btn btn-md btn-primary link"
					onClick={onClick}
				>
					Retry
				</button>
			</div>
		) : seconds < 10 ? (
			<HeartLoader />
		) : seconds < 20 ? (
			<div className="online-booking__error-code-component">
				<div className="text">
					<p>Please bear with us - things are taking a bit longer than usual. Thank you for your patience.</p>
				</div>
				<HeartLoader />
			</div>
		) : null
	) : (
		<>{children}</>
	);
};

export default BookingLoader;
